import React, { useEffect, useMemo, useState } from "react";

import { motion } from "framer-motion";
import { chunk, uniqBy } from "lodash";
import { useStep } from "usehooks-ts";

import { GenericTooltip } from "@components/tooltip-container";
import { useDesignElementManager } from "@hooks/use-design-element-manager";
import { TrackPerformanceTrackedComponent } from "@pages/mmm/amplitude-tracking/components";
import { MMMTab } from "@pages/mmm/amplitude-tracking/mmm-event";
import { useTrackMMMEvent } from "@pages/mmm/amplitude-tracking/use-track-mmm-event";
import { TertiaryButton } from "@shared/buttons";

import { type PerformanceTrackingDataset } from "./utils";

const MAX_PER_ROW = 4;

export function TrackingChartSplitLegend({
  datasets,
}: {
  datasets: PerformanceTrackingDataset[];
}) {
  const trackMMMEvent = useTrackMMMEvent(MMMTab.TRACK_PERFORMANCE);
  const { designElementManager } = useDesignElementManager();

  // HACK: This is a hack to force the legend to re-render when the chart is updated
  const [HACK, setHACK] = useState(0);
  useEffect(() => {
    if (datasets.length !== 0 && datasets.length < 2) {
      setHACK((prev) => prev + 1);
    }
  }, [HACK, datasets]);

  const splitLegend = useMemo(() => {
    return uniqBy(
      datasets.map(({ metadata }) => ({
        breakdowns: metadata.breakdowns,
        color: metadata.color,
      })),
      (e) => Object.values(e.breakdowns).join(""),
    );
  }, [datasets]);

  const pages = chunk(splitLegend, MAX_PER_ROW);
  const [
    currentPage,
    { canGoToPrevStep, canGoToNextStep, goToNextStep, reset },
  ] = useStep(pages.length);
  const maxVisibleLegendCount = currentPage * MAX_PER_ROW;

  const leftOverMetricsCount = splitLegend.length - maxVisibleLegendCount;
  const nextStepCount =
    leftOverMetricsCount < MAX_PER_ROW ? leftOverMetricsCount : MAX_PER_ROW;

  return (
    <div className="flex items-center space-x-2 max-w-2xl mr-[100px]">
      <div
        className="grid gap-3 py-1.5"
        style={{
          gridTemplateColumns: `repeat(${
            splitLegend.length > MAX_PER_ROW ? MAX_PER_ROW : splitLegend.length
          }, 1fr)`,
        }}
      >
        {splitLegend.map((legend, index) => {
          if (index >= maxVisibleLegendCount) {
            return null;
          }

          const label = Object.values(legend.breakdowns).join(" / ");
          const icon =
            Object.keys(legend.breakdowns)[0] === "Platform (Northbeam)" ? (
              designElementManager
                .getDesignElement(legend.breakdowns["Platform (Northbeam)"])
                .getComponent({ size: "20px" })
            ) : (
              <div
                className="h-3 w-3"
                style={{ backgroundColor: legend.color }}
              />
            );

          return (
            <GenericTooltip
              key={JSON.stringify(legend.breakdowns)}
              noInfoCircle
              content={label}
            >
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2, ease: "easeIn" }}
                className="flex items-center space-x-1 font-light"
              >
                {icon}
                <div className="truncate max-w-[120px]">{label}</div>
              </motion.div>
            </GenericTooltip>
          );
        })}
      </div>
      <div className="flex flex-col justify-between h-full">
        {canGoToPrevStep ? (
          <TertiaryButton
            size="sm"
            onClick={() => {
              trackMMMEvent({
                component: TrackPerformanceTrackedComponent.SPEND_CHART,
                action: "onClick",
                actionInfo: "Collapse legend",
              });

              reset();
            }}
          >
            COLLAPSE
          </TertiaryButton>
        ) : (
          <div />
        )}
        {canGoToNextStep && (
          <TertiaryButton
            size="sm"
            onClick={() => {
              trackMMMEvent({
                component: TrackPerformanceTrackedComponent.SPEND_CHART,
                action: "onClick",
                actionInfo: "See more legend options",
              });

              goToNextStep();
            }}
          >
            NEXT {nextStepCount}
          </TertiaryButton>
        )}
      </div>
    </div>
  );
}
